import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RotateCw, Zap, FileJson, Loader2, Lock } from "lucide-react";
import { generateIdempotencyKey, triggerSaga } from "../services/saga";
import { listSagaDefinitions } from "../services/saga-definition";
import {
  getPlaceholderRequirements,
  upsertTenantSagaVariables,
  type PlaceholderRequirement,
  type SagaVariableValueType,
} from "../services/saga-variables";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select } from "../components/ui/select";
import { FIELD_CLS, FIELD_LABEL_CLS, HELP_CLS, TEXTAREA_CLS } from "../components/chrome/fieldChrome";
import { cn } from "../utils/cn";
import { SAGA_BY_NAME, exampleAsJson } from "./_lib/saga-catalog";

interface MissingVarsState {
  sagaType: string;
  tenantKey: string | null;
  parsedInput: unknown;
  idempotencyKey: string;
  missing: PlaceholderRequirement[];
}

export default function SagaNew() {
  const navigate = useNavigate();
  const [sagaType, setSagaType] = useState("EchoSaga");
  const [inputJson, setInputJson] = useState(exampleAsJson("EchoSaga"));
  const [idempotencyKey, setIdempotencyKey] = useState(generateIdempotencyKey());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [missingState, setMissingState] = useState<MissingVarsState | null>(null);
  const [definitionNames, setDefinitionNames] = useState<string[]>([]);

  // Carrega definições active do DB (SPEC-2300: source of truth pra sagaType).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const defs = await listSagaDefinitions({ active: true });
        if (alive) setDefinitionNames(defs.map((d) => d.name));
      } catch (e) {
        // Falha silenciosa — fallback é catálogo estático.
        // eslint-disable-next-line no-console
        console.warn("Falha ao listar saga definitions:", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Merge: união de definitions do DB + catálogo estático (test sagas + exemplos).
  // DB é fonte da verdade pra "o que pode disparar agora".
  const sagaOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: Array<{ value: string; label: string }> = [];
    // Primeiro as definitions do DB (preferencial — tem versionamento active)
    for (const name of definitionNames) {
      if (seen.has(name)) continue;
      seen.add(name);
      opts.push({ value: name, label: name });
    }
    // Depois catálogo estático (cobre test sagas + sagas em código sem definition)
    for (const entry of Object.values(SAGA_BY_NAME)) {
      if (seen.has(entry.name)) continue;
      seen.add(entry.name);
      opts.push({ value: entry.name, label: entry.name });
    }
    return opts;
  }, [definitionNames]);

  const entry = SAGA_BY_NAME[sagaType];

  /** Troca o tipo + reseta o JSON pro exemplo daquela saga + idempotency key novo. */
  const onSagaTypeChange = (next: string) => {
    setSagaType(next);
    setInputJson(exampleAsJson(next));
    setIdempotencyKey(generateIdempotencyKey());
    setErr(null);
  };

  const onResetExample = () => {
    setInputJson(exampleAsJson(sagaType));
    setErr(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!sagaType.trim()) return setErr("sagaType obrigatório");
    if (!idempotencyKey.trim()) return setErr("idempotencyKey obrigatório");

    let parsedInput: unknown;
    try {
      parsedInput = JSON.parse(inputJson);
    } catch {
      return setErr("Input não é JSON válido");
    }

    setSubmitting(true);
    try {
      const result = await triggerSaga({
        sagaType: sagaType.trim(),
        input: parsedInput,
        idempotencyKey: idempotencyKey.trim(),
      });
      toast.success(`Saga ${result.sagaType} disparada — status ${result.status}`);
      navigate(`/sagas/${result.sagaId}`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Falha ao disparar saga";
      // SPEC-20260507-2200: backend retorna 400 com mensagem listando placeholders
      // missing. Detecta padrão e abre modal pra completar.
      if (msg.includes("Variáveis obrigatórias faltando")) {
        try {
          const tenantKey = (parsedInput as { tenantKey?: string })?.tenantKey ?? null;
          const reqs = await getPlaceholderRequirements(sagaType.trim(), tenantKey);
          const missing = reqs.required.filter((r) => !r.hasValue);
          if (missing.length > 0) {
            setMissingState({
              sagaType: sagaType.trim(),
              tenantKey,
              parsedInput,
              idempotencyKey: idempotencyKey.trim(),
              missing,
            });
            return;
          }
        } catch {
          /* fallback pra erro normal */
        }
      }
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  async function handleCompleteAndTrigger(answers: Record<string, string>, persistToTenant: boolean) {
    if (!missingState) return;
    setSubmitting(true);
    try {
      // 1. Persiste no tenant se aplicável + checkbox.
      if (persistToTenant && missingState.tenantKey) {
        const variables: Record<string, { valueType: SagaVariableValueType; value: string; isSensitive?: boolean }> = {};
        for (const m of missingState.missing) {
          const v = answers[m.name];
          if (v && v.trim().length > 0) {
            variables[m.name] = {
              valueType: m.type,
              value: v.trim(),
              isSensitive: m.isSensitive,
            };
          }
        }
        if (Object.keys(variables).length > 0) {
          await upsertTenantSagaVariables(missingState.tenantKey, missingState.sagaType, variables);
          toast.success("Valores persistidos no tenant");
        }
      }
      // 2. Dispara saga com input contendo __runtimeValues (override execução).
      const inputWithRuntime = {
        ...(missingState.parsedInput as Record<string, unknown>),
        __runtimeValues: {
          ...((missingState.parsedInput as { __runtimeValues?: Record<string, string> })?.__runtimeValues ?? {}),
          ...answers,
        },
      };
      const result = await triggerSaga({
        sagaType: missingState.sagaType,
        input: inputWithRuntime,
        idempotencyKey: missingState.idempotencyKey,
      });
      toast.success(`Saga ${result.sagaType} disparada — status ${result.status}`);
      setMissingState(null);
      navigate(`/sagas/${result.sagaId}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao disparar");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Disparar saga"
        subtitle="Inicia uma execução de saga (pra criar uma nova definição, vá em Definitions)"
        actions={
          <Link to="/sagas"><Button size="sm" variant="outline"><ArrowLeft size={14} /> Sagas</Button></Link>
        }
      />

      <div className="p-6">
        <form onSubmit={onSubmit} className="max-w-2xl space-y-4 rounded-md border border-border bg-surface p-6 shadow-sm">
          <div className={FIELD_CLS}>
            <Label htmlFor="sagaType">Saga type</Label>
            <Select
              id="sagaType"
              value={sagaType}
              options={sagaOptions}
              onChange={(v) => onSagaTypeChange(String(v))}
              className="font-mono-tight"
            />
            {entry && (
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{entry.description}</p>
            )}
          </div>

          {entry && entry.fields.length > 0 && (
            <div className="rounded-md border border-border-subtle bg-surface-subtle/40 p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Campos esperados</p>
              <ul className="space-y-1.5">
                {entry.fields.map((f) => (
                  <li key={f.name} className="text-xs flex items-start gap-2 leading-relaxed">
                    <code className="font-mono-tight text-foreground shrink-0">
                      {f.name}
                      {f.required && <span className="text-danger ml-0.5">*</span>}
                    </code>
                    <span className="text-muted-foreground">
                      <code className="font-mono-tight">{f.type}</code>
                      {f.note && <span className="ml-1">— {f.note}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={FIELD_CLS}>
            <div className="flex items-center justify-between">
              <Label htmlFor="input">Input (JSON)</Label>
              <button
                type="button"
                onClick={onResetExample}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                title="Restaurar exemplo padrão desta saga"
              >
                <FileJson size={11} /> Resetar pro exemplo
              </button>
            </div>
            <textarea
              id="input"
              value={inputJson}
              onChange={(e) => setInputJson(e.target.value)}
              rows={8}
              className={cn(TEXTAREA_CLS, "font-mono text-xs leading-relaxed")}
              spellCheck={false}
            />
            <span className={HELP_CLS}>Será enviado como `input` para o primeiro step da saga. Edite os valores conforme necessário.</span>
          </div>

          <div className={FIELD_CLS}>
            <span className={FIELD_LABEL_CLS}>Idempotency Key</span>
            <div className="flex gap-2">
              <Input id="idempotency" value={idempotencyKey} onChange={(e) => setIdempotencyKey(e.target.value)} required className="font-mono-tight" />
              <Button type="button" variant="outline" size="md" onClick={() => setIdempotencyKey(generateIdempotencyKey())}>
                <RotateCw size={13} />
              </Button>
            </div>
            <span className={HELP_CLS}>Mesma key + mesmo input → mesma saga (idempotência).</span>
          </div>

          {err && <p className="text-xs text-danger">{err}</p>}

          <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
            <Link to="/sagas"><Button type="button" variant="ghost">Cancelar</Button></Link>
            <Button type="submit" disabled={submitting}>
              <Zap size={14} />
              {submitting ? "Disparando…" : "Disparar saga"}
            </Button>
          </div>
        </form>
      </div>

      {missingState && (
        <CompleteVariablesModal
          state={missingState}
          submitting={submitting}
          onClose={() => setMissingState(null)}
          onConfirm={handleCompleteAndTrigger}
        />
      )}
    </>
  );
}

interface CompleteVariablesModalProps {
  state: MissingVarsState;
  submitting: boolean;
  onClose(): void;
  onConfirm(answers: Record<string, string>, persistToTenant: boolean): void;
}

function CompleteVariablesModal({ state, submitting, onClose, onConfirm }: CompleteVariablesModalProps) {
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const m of state.missing) if (m.default !== undefined) init[m.name] = m.default;
    return init;
  });
  const [persistToTenant, setPersistToTenant] = useState(true);

  function setVal(name: string, v: string) {
    setAnswers((prev) => ({ ...prev, [name]: v }));
  }

  function allFilled(): boolean {
    return state.missing.every((m) => (answers[m.name] ?? "").trim().length > 0);
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-1 rounded-lg shadow-lg w-full max-w-lg p-4 border border-border max-h-[90vh] overflow-y-auto">
        <h2 className="font-medium text-sm mb-1">Variáveis faltando pra saga {state.sagaType}</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Preencha {state.missing.length} variável(is) requerida(s){" "}
          {state.tenantKey ? `pro tenant '${state.tenantKey}'` : "(execução sem tenant)"}.
        </p>

        <div className="space-y-3">
          {state.missing.map((m) => (
            <div key={m.name}>
              <Label htmlFor={`miss-${m.name}`} className="flex items-center gap-1.5 text-xs">
                {m.isSensitive && <Lock size={11} className="text-danger" />}
                <code className="font-mono-tight">{m.name}</code>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {m.type}
                </span>
              </Label>
              {m.description && (
                <p className="text-[11px] text-muted-foreground mt-0.5">{m.description}</p>
              )}
              <Input
                id={`miss-${m.name}`}
                type={m.isSensitive ? "password" : m.type === "number" ? "number" : "text"}
                value={answers[m.name] ?? ""}
                onChange={(e) => setVal(m.name, e.target.value)}
                placeholder={m.default ? `default: ${m.default}` : ""}
                className="mt-1"
              />
            </div>
          ))}
        </div>

        {state.tenantKey && (
          <label className="flex items-center gap-2 text-xs mt-4 cursor-pointer">
            <input
              type="checkbox"
              checked={persistToTenant}
              onChange={(e) => setPersistToTenant(e.target.checked)}
            />
            Persistir valores no tenant '{state.tenantKey}' pra próximas execuções
            (recomendado)
          </label>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => onConfirm(answers, persistToTenant)}
            disabled={!allFilled() || submitting}
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            Salvar + disparar
          </Button>
        </div>
      </div>
    </div>
  );
}
